import React, { useState, useEffect } from 'react';
import { Eye, Filter, Search, Calendar, User, BookOpen, FileText, GraduationCap, X, Star, CheckCircle, AlertCircle } from 'lucide-react';

// Grading Interface Component
const GradingInterface = ({ homework, submissions, onClose, onGradeComplete }) => {
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [score, setScore] = useState(homework.score || '');
  const [feedback, setFeedback] = useState(homework.feedback || '');
  const [isGrading, setIsGrading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleGradeSubmit = async () => {
    if (!score || score < 0 || score > 100) {
      setError('Please enter a valid score between 0 and 100');
      return;
    }

    setIsGrading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      
      // Update the homework with the new score and feedback
      const response = await fetch(`${process.env.REACT_APP_API_URL}/homework/${homework.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          score: score.toString(),
          feedback: feedback,
          status: 'GRADED'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit grade');
      }

      const updatedHomework = await response.json();
      setSuccess(true);
      
      // Call the callback to refresh the parent component
      if (onGradeComplete) {
        onGradeComplete(updatedHomework);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setIsGrading(false);
    }
  };

  const handleAIGrade = async () => {
    if (!submissions || submissions.length === 0) {
      setError('No submissions available for AI grading');
      return;
    }

    setIsGrading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      
      // Call the AI grading endpoint
      const response = await fetch(`${process.env.REACT_APP_API_URL}/homework/${homework.id}/grade`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'AI grading failed');
      }

      const result = await response.json();
      
      if (result.score) {
        setScore(result.score.toString());
        // If AI returns feedback, populate it
        if (result.feedback) {
          setFeedback(result.feedback);
        }
        setSuccess(true);
        
        if (onGradeComplete) onGradeComplete(result);
      } else {
        setError(result.error || 'AI grading did not return a score');
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setIsGrading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl bg-white shadow-2xl rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              Grade Homework
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {homework.subject} - Student {homework.studentId}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/50 transition-colors"
            disabled={isGrading}
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {!success ? (
            <>
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">Grade Assignment</h3>
                
                {/* Score Input */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Score (0-100)
                  </label>
                  <div className="flex items-center space-x-4">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={score}
                      onChange={(e) => setScore(e.target.value)}
                      className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter score"
                    />
                    <span className="text-sm text-gray-500">/ 100</span>
                  </div>
                </div>

                {/* Feedback Input */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Feedback (Optional)
                  </label>
                  <textarea
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    maxLength={2000}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Provide feedback for the student..."
                  />
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-xs text-gray-500">
                      Share comments, suggestions, or areas for improvement
                    </p>
                    <p className={`text-xs ${feedback.length > 1900 ? 'text-orange-600 font-medium' : 'text-gray-500'}`}>
                      {feedback.length}/2000
                    </p>
                  </div>
                </div>

                {/* AI Grading Option */}
                {submissions && submissions.length > 0 && (
                  <div className="mb-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-medium text-blue-900 mb-2 flex items-center">
                        <Star className="w-4 h-4 mr-2" />
                        AI Grading Assistant
                      </h4>
                      <p className="text-sm text-blue-800 mb-3">
                        Use AI to automatically grade this assignment and generate feedback based on the submitted work.
                      </p>
                      <button
                        onClick={handleAIGrade}
                        disabled={isGrading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center text-sm"
                      >
                        {isGrading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            AI Grading...
                          </>
                        ) : (
                          <>
                            <Star className="w-4 h-4 mr-2" />
                            Grade with AI
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* Submissions Preview */}
                {submissions && submissions.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Available Submissions</h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {submissions.map((submission, index) => (
                        <div
                          key={submission.id}
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            selectedSubmission?.id === submission.id
                              ? 'bg-blue-50 border-blue-300'
                              : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                          }`}
                          onClick={() => setSelectedSubmission(submission)}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-900">
                              {submission.fileName || `Submission ${index + 1}`}
                            </span>
                            {selectedSubmission?.id === submission.id && (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            )}
                          </div>
                          <p className="text-xs text-gray-600 mt-1">
                            {new Date(submission.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-red-900 mb-1">Error</h4>
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                  disabled={isGrading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleGradeSubmit}
                  disabled={isGrading || !score}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center"
                >
                  {isGrading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Submit Grade
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Grade Submitted Successfully!
              </h3>
              <p className="text-gray-600 mb-2">
                The homework has been graded with a score of <span className="font-bold text-green-600">{score}/100</span>.
              </p>
              {feedback && (
                <div className="bg-gray-50 rounded-lg p-4 mt-4 text-left max-w-md mx-auto">
                  <p className="text-sm font-medium text-gray-700 mb-2">Feedback provided:</p>
                  <p className="text-sm text-gray-600">{feedback}</p>
                </div>
              )}
              <button
                onClick={onClose}
                className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Homework Review Interface Component
const HomeworkReviewInterface = ({ homework, onClose, onGradeClick }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentHomework, setCurrentHomework] = useState(homework);

  useEffect(() => {
    setCurrentHomework(homework);
  }, [homework]);

  useEffect(() => {
    fetchSubmissions();
  }, [homework.id]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/homework/${homework.id}/submissions`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch submissions: ${response.status}`);
      }

      const submissionsData = await response.json();
      setSubmissions(submissionsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewSubmission = async (submissionId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/homework/submissions/${submissionId}/image`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to load image: ${response.status}`);
      }

      const blob = await response.blob();
      const imageUrl = URL.createObjectURL(blob);
      
      // Open image in new tab
      const newWindow = window.open();
      newWindow.document.write(`
        <html>
          <head><title>Submission Image</title></head>
          <body style="margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f5f5f5;">
            <img src="${imageUrl}" style="max-width: 100%; max-height: 100vh;" alt="Submission" />
          </body>
        </html>
      `);
    } catch (err) {
      setError('Failed to view submission: ' + err.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl bg-white shadow-2xl rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              Homework Review
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {currentHomework.subject} - Student ID: {currentHomework.studentId}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/50 transition-colors"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {/* Homework Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Assignment Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subject:</span>
                  <span className="font-medium">{currentHomework.subject}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Student ID:</span>
                  <span className="font-medium">{currentHomework.studentId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Due Date:</span>
                  <span className="font-medium">{formatDate(currentHomework.dueDate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`font-medium px-2 py-1 rounded text-xs ${
                    currentHomework.status === 'GRADED' || currentHomework.status === 'Completed' ? 'bg-green-100 text-green-800' :
                    currentHomework.status === 'Late' ? 'bg-red-100 text-red-800' :
                    currentHomework.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {currentHomework.status || 'Assigned'}
                  </span>
                </div>
                {currentHomework.score && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Score:</span>
                    <span className="font-bold text-green-600">{currentHomework.score}/100</span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Description</h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {currentHomework.description || 'No description provided.'}
              </p>
            </div>
          </div>

          {/* Feedback Section */}
          {currentHomework.feedback && (
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">Tutor Feedback</h3>
              <p className="text-sm text-blue-800 whitespace-pre-wrap">
                {currentHomework.feedback}
              </p>
            </div>
          )}

          {/* Submissions Section */}
          <div className="border-t pt-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-gray-900 text-lg">Student Submissions</h3>
              {submissions.length > 0 && currentHomework.status !== 'GRADED' && (
                <button
                  onClick={() => onGradeClick(currentHomework, submissions)}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center text-sm"
                >
                  <Star className="w-4 h-4 mr-2" />
                  Grade Assignment
                </button>
              )}
            </div>
            
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading submissions...</p>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <p className="text-red-800">{error}</p>
                <button
                  onClick={fetchSubmissions}
                  className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                >
                  Retry
                </button>
              </div>
            ) : submissions.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                <FileText className="w-12 h-12 text-yellow-400 mx-auto mb-3" />
                <h4 className="font-medium text-yellow-900 mb-1">No Submissions Yet</h4>
                <p className="text-yellow-800 text-sm">
                  The student hasn't submitted any work for this assignment.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {submissions.map((submission, index) => (
                  <div key={submission.id} className="border rounded-lg overflow-hidden bg-white hover:shadow-md transition-shadow">
                    <div className="bg-gray-100 p-3 border-b">
                      <h4 className="font-medium text-sm text-gray-900 truncate">
                        {submission.fileName || `Submission ${index + 1}`}
                      </h4>
                      <p className="text-xs text-gray-600 mt-1">
                        Submitted: {formatDate(submission.createdAt)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Type: {submission.contentType || 'Image'}
                      </p>
                    </div>
                    <div className="p-4">
                      <button
                        onClick={() => handleViewSubmission(submission.id)}
                        className="w-full px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center text-sm"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Image
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}
        </div>

        <div className="border-t bg-gray-50 p-6">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Homework Dashboard Component
const HomeworkDashboard = () => {
  const [homeworks, setHomeworks] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [selectedStudent, setSelectedStudent] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('dueDate');
  
  // Modal states
  const [selectedHomework, setSelectedHomework] = useState(null);
  const [selectedSubmissions, setSelectedSubmissions] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showGradingModal, setShowGradingModal] = useState(false);

  useEffect(() => {
    fetchHomework();
  }, []);

  const fetchHomework = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      
      // Fetch all homework for the current tutor
      const response = await fetch(`${process.env.REACT_APP_API_URL}/homework/tutor/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error(`Failed to fetch homework: ${response.status}`);
      
      const homeworkData = await response.json();
      setHomeworks(homeworkData);
      
      // Extract unique students by ID since we don't have names
      const uniqueStudentIds = [...new Set(homeworkData.map(hw => hw.studentId).filter(Boolean))];
      const uniqueStudents = uniqueStudentIds.map(id => ({ id, name: `Student ${id}` }));
      setStudents(uniqueStudents);
      
    } catch (err) {
      console.error('Error fetching homework:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewClick = (homework) => {
    setSelectedHomework(homework);
    setShowReviewModal(true);
  };

  const handleGradeClick = (homework, submissions) => {
    setSelectedHomework(homework);
    setSelectedSubmissions(submissions);
    setShowGradingModal(true);
    setShowReviewModal(false);
  };

  const handleGradeComplete = (updatedHomework) => {
    // Update the homework list with the graded homework
    setHomeworks(prevHomeworks =>
      prevHomeworks.map(hw =>
        hw.id === updatedHomework.id ? updatedHomework : hw
      )
    );
    // Update the selected homework with new values
    setSelectedHomework(updatedHomework);
    setShowGradingModal(false);
    setShowReviewModal(true); // Reopen review modal with updated data
    setSelectedSubmissions([]);
  };

  // Filter and sort homework
  const filteredHomework = homeworks
    .filter(hw => {
      if (selectedStudent !== 'all' && hw.studentId.toString() !== selectedStudent) return false;
      if (statusFilter !== 'all') {
        if (statusFilter === 'Completed') {
          if (hw.status !== 'GRADED' && hw.status !== 'Completed') return false;
        } else {
          if (hw.status !== statusFilter) return false;
        }
      }
      if (searchTerm && 
          !hw.subject.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !hw.studentId.toString().includes(searchTerm)) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'dueDate') {
        return new Date(a.dueDate) - new Date(b.dueDate);
      } else if (sortBy === 'student') {
        return a.studentId - b.studentId;
      } else if (sortBy === 'subject') {
        return a.subject.localeCompare(b.subject);
      }
      return 0;
    });

  const getStatusColor = (status) => {
    switch (status) {
      case 'GRADED':
      case 'Completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Late':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'In Progress':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Assigned':
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return 'No due date';
    
    const today = new Date();
    const due = new Date(dueDate);
    
    if (isNaN(due.getTime())) return 'Invalid due date';
    
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    return `${diffDays} days left`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading homework assignments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
            <GraduationCap className="w-8 h-8 mr-3 text-blue-600" />
            Homework Management
          </h1>
          <p className="text-gray-600">View, review, and grade student homework assignments</p>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Subject or student ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>
            </div>

            {/* Student Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Student</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <select
                  value={selectedStudent}
                  onChange={(e) => setSelectedStudent(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                >
                  <option value="all">All Students</option>
                  {students.map((student) => (
                    <option key={student.id} value={student.id.toString()}>
                      {student.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                >
                  <option value="all">All Statuses</option>
                  <option value="Assigned">Assigned</option>
                  <option value="In Progress">In Progress</option>
                  <option value="GRADED">Completed</option>
                  <option value="Late">Late</option>
                </select>
              </div>
            </div>

            {/* Sort By */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="dueDate">Due Date</option>
                <option value="student">Student ID</option>
                <option value="subject">Subject</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4 text-sm text-gray-600">
          Showing {filteredHomework.length} of {homeworks.length} assignments
        </div>

        {/* Homework Grid */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
            <button
              onClick={fetchHomework}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
            >
              Try Again
            </button>
          </div>
        )}

        {filteredHomework.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No homework found</h3>
            <p className="text-gray-600">Try adjusting your filters or create new homework assignments</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredHomework.map((homework) => (
              <div
                key={homework.id}
                className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow border border-gray-200 overflow-hidden"
              >
                {/* Card Header */}
                <div className="p-5 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-semibold text-lg text-gray-900">
                      {homework.subject}
                    </h3>
                    <span className={`text-xs px-2 py-1 rounded border ${getStatusColor(homework.status)}`}>
                      {homework.status === 'GRADED' ? 'Completed' : homework.status}
                    </span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <User className="w-4 h-4 mr-1" />
                    Student {homework.studentId}
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-5">
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                      <span className="font-medium mr-2">Due:</span>
                      {formatDate(homework.dueDate)}
                    </div>
                    <div className="text-sm">
                      <span className={`font-medium ${
                        getDaysUntilDue(homework.dueDate).includes('overdue') 
                          ? 'text-red-600' 
                          : 'text-blue-600'
                      }`}>
                        {getDaysUntilDue(homework.dueDate)}
                      </span>
                    </div>
                    {homework.score && (
                      <div className="flex items-center text-sm">
                        <span className="font-medium mr-2">Score:</span>
                        <span className="text-green-600 font-bold">{homework.score}/100</span>
                      </div>
                    )}
                    {homework.description && (
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {homework.description}
                      </p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-2">
                    <button
                      onClick={() => handleReviewClick(homework)}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Review Homework
                    </button>
                    {homework.status !== 'GRADED' && (
                      <button
                        onClick={() => handleReviewClick(homework)}
                        className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center text-sm"
                      >
                        <Star className="w-4 h-4 mr-2" />
                        Grade Assignment
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Review Modal */}
        {showReviewModal && selectedHomework && (
          <HomeworkReviewInterface
            homework={selectedHomework}
            onClose={() => {
              setShowReviewModal(false);
              setSelectedHomework(null);
            }}
            onGradeClick={handleGradeClick}
          />
        )}

        {/* Grading Modal */}
        {showGradingModal && selectedHomework && (
          <GradingInterface
            homework={selectedHomework}
            submissions={selectedSubmissions}
            onClose={() => {
              setShowGradingModal(false);
              setSelectedHomework(null);
              setSelectedSubmissions([]);
            }}
            onGradeComplete={handleGradeComplete}
          />
        )}
      </div>
    </div>
  );
};

export default HomeworkDashboard;